import type { ReactNode } from "react";
import { FORM_LABEL_LINK_CLASS } from "../../theme/typographyTokens";

/**
 * Safe inline links for form labels / radio option text. No raw HTML.
 * Supported syntaxes (both http/https only):
 * - Markdown: `[Privacy policy](https://example.com/privacy)`
 * - Pipe (matches interlink `[[slug|label]]` convention users expect):
 *   `[Privacy policy | https://example.com/privacy]`
 */
const FORM_LABEL_LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|\[([^\]|]+)\|\s*(https?:\/\/[^\]\s]+)\s*\]/g;
export type FormLabelSegment = {
  type: "text";
  value: string;
} | {
  type: "link";
  text: string;
  href: string;
};
export function parseFormLabelSegments(input: string): FormLabelSegment[] {
  const text = input ?? "";
  if (!text) {
    return [];
  }
  const segments: FormLabelSegment[] = [];
  let lastIndex = 0;
  FORM_LABEL_LINK_RE.lastIndex = 0;
  let match = FORM_LABEL_LINK_RE.exec(text);
  while (match) {
    const start = match.index;
    if (start > lastIndex) {
      segments.push({
        type: "text",
        value: text.slice(lastIndex, start)
      });
    }
    const linkText = (match[1] ?? match[3] ?? "").trim();
    const linkHref = match[2] ?? match[4] ?? "";
    segments.push({
      type: "link",
      text: linkText,
      href: linkHref
    });
    lastIndex = start + match[0].length;
    match = FORM_LABEL_LINK_RE.exec(text);
  }
  if (lastIndex < text.length) {
    segments.push({
      type: "text",
      value: text.slice(lastIndex)
    });
  }
  return segments;
}

/** Plain text for native `<option>` (no nested anchors). */
export function formLabelPlainText(input: string): string {
  return parseFormLabelSegments(input).map(s => s.type === "text" ? s.value : s.text).join("");
}
type FormLabelWithLinksProps = {
  text: string | null | undefined;
  /** When false, links are not clickable (builder canvas preview). */
  interactive?: boolean;
};
export function FormLabelWithLinks({
  text,
  interactive = true
}: FormLabelWithLinksProps): ReactNode {
  const raw = text ?? "";
  if (!raw) {
    return null;
  }
  const segments = parseFormLabelSegments(raw);
  if (segments.length === 0) {
    return raw;
  }
  if (segments.length === 1 && segments[0]?.type === "text") {
    return segments[0].value;
  }
  return segments.map((segment, index) => {
    if (segment.type === "text") {
      return <span key={`t-${index}-${segment.value.slice(0, 12)}`}>
					{segment.value}
				</span>;
    }
    if (!interactive) {
      return <span className={FORM_LABEL_LINK_CLASS} key={`l-${index}-${segment.href}`} style={{
        color: "var(--tt-color-primary, #2563eb)",
        textDecoration: "underline"
      }}>
					{segment.text}
				</span>;
    }
    return <a className={FORM_LABEL_LINK_CLASS} href={segment.href} key={`a-${index}-${segment.href}`} onClick={e => e.stopPropagation()} rel="noopener noreferrer" style={{
      color: "var(--tt-color-primary, #2563eb)",
      textDecoration: "underline"
    }} target="_blank">
				{segment.text}
			</a>;
  });
}